import { Factory } from '../Factory';
import { SearchExpression, Operator } from '../../model/search/advanced/SearchExpression';
import { ComparisonType } from '../../model/search/advanced/ComparisonType';
import { MultiTextField } from '../../model/search/advanced/MultiTextField';
import { MultiFullTextField } from '../../model/search/advanced/MultiFullTextField';
import { ListItem } from '../../model/controls/ListItem';

namespace app.services.speech {

  export class WebSpeechService {
    private readonly $rootScope: IRootScope;
    private readonly recognition: SpeechRecognition;

    constructor($rootScope) {
      this.$rootScope = $rootScope;
      const SpeechRecognition = window["webkitSpeechRecognition"] || window["SpeechRecognition"];

      if (SpeechRecognition) {
        this.recognition = new SpeechRecognition();
        this.recognition.lang = "en-US";
        this.recognition.continuous = true;
        this.recognition.interimResults = false;


        this.registerSpeechRecognitionEvents();
      }
    }

    public start() {
      if (this.recognition) {
        this.recognition.start();
      }
    }

    public stop() {
      if (this.recognition) {
        this.recognition.stop();
      }
    }

    public speak(message) {
      if (window["speechSynthesis"]) {
        if (speechSynthesis.speaking) {
          speechSynthesis.cancel();
        }

        const utterance = new SpeechSynthesisUtterance(message);
        utterance.volume = 0.3;
        utterance.rate = 1.1;


        speechSynthesis.speak(utterance);
      }
    }

    private readonly submitSearch = (searchExpression: SearchExpression) => {
      this.$rootScope.$broadcast("Search.SearchExecuted", {
        advanced: true,
        expression: searchExpression
      });

      const responses = ["Ok", "Done", "Here you go", "There you go", "Here they are"]

      this.speak(responses[Math.floor(Math.random() * Math.floor(responses.length))]);
    };

    private readonly command1 = term => {
      const searchExpression = new SearchExpression();

      switch (term) {
        case "emails":
          searchExpression.operator = Operator.And;
          searchExpression.fields.push(new MultiTextField("type", null, [new ListItem("email", null)], ComparisonType.Equals));
          break;
        case "communications":
          searchExpression.operator = Operator.Or;
          searchExpression.fields.push(new MultiTextField("type", null, [new ListItem("email", null), new ListItem("sms", null), new ListItem("mms", null), new ListItem("instant message", null)], ComparisonType.Equals));
          break;
        case "messages":
        case "text messages":
          searchExpression.operator = Operator.Or;
          searchExpression.fields.push(new MultiTextField("type", null, [new ListItem("sms", null), new ListItem("mms", null), new ListItem("instant message", null)], ComparisonType.Equals));
          break;
        case "sms":
        case "sms messages":
          searchExpression.operator = Operator.Or;
          searchExpression.fields.push(new MultiTextField("type", null, [new ListItem("sms", null)], ComparisonType.Equals));
          break;
        case "mms":
        case "mms messages":
          searchExpression.operator = Operator.Or;
          searchExpression.fields.push(new MultiTextField("type", null, [new ListItem("mms", null)], ComparisonType.Equals));
          break;
        case "instant messages":
          searchExpression.operator = Operator.Or;
          searchExpression.fields.push(new MultiTextField("type", null, [new ListItem("instant message", null)], ComparisonType.Equals));
          break;
        case "voicemails":
          searchExpression.operator = Operator.And;
          searchExpression.fields.push(new MultiTextField("type", null, [new ListItem("voicemail", null)], ComparisonType.Equals));
          break;
        case "images":
          searchExpression.operator = Operator.And;
          searchExpression.fields.push(new MultiTextField("category", null, [new ListItem("Bitmap images", null)], ComparisonType.Equals));
          break;
        case "video files":
        case "videos":
          searchExpression.operator = Operator.And;
          searchExpression.fields.push(new MultiTextField("category", null, [new ListItem("Video", null)], ComparisonType.Equals));
          break;
        case "audio files":
        case "audio":
          searchExpression.operator = Operator.And;
          searchExpression.fields.push(new MultiTextField("category", null, [new ListItem("Audio", null)], ComparisonType.Equals));
          break;
        case "media":
        case "media files":
          searchExpression.operator = Operator.Or;
          searchExpression.fields.push(new MultiTextField("category", null, [new ListItem("Video", null)], ComparisonType.Equals));
          searchExpression.fields.push(new MultiTextField("category", null, [new ListItem("Bitmap images", null)], ComparisonType.Equals));
          searchExpression.fields.push(new MultiTextField("category", null, [new ListItem("Audio", null)], ComparisonType.Equals));
          break;
        default:
          break;
      }

      this.submitSearch(searchExpression);
    };

    private readonly command2 = term => {
      const searchExpression = new SearchExpression();

      searchExpression.operator = Operator.And;
      searchExpression.fields.push(new MultiTextField("type", null, [new ListItem("email", null)], ComparisonType.Equals));
      searchExpression.fields.push(new MultiTextField("emailFrom", null, [new ListItem(term, null)], ComparisonType.Contains));

      this.submitSearch(searchExpression);
    };

    private readonly command3 = term => {
      const searchExpression = new SearchExpression();

      searchExpression.operator = Operator.And;
      searchExpression.fields.push(new MultiTextField("emailFrom", null, [new ListItem(term, null)], ComparisonType.Contains));
      searchExpression.fields.push(new MultiTextField("type", null, [new ListItem("email", null), new ListItem("sms", null), new ListItem("mms", null), new ListItem("instant message", null)], ComparisonType.Equals));

      this.submitSearch(searchExpression);
    };

    private readonly command4 = term => {
      const searchExpression = new SearchExpression();

      searchExpression.operator = Operator.And;
      searchExpression.fields.push(new MultiTextField("emailFrom", null, [new ListItem(term, null)], ComparisonType.Contains));
      searchExpression.fields.push(new MultiTextField("type", null, [new ListItem("sms", null), new ListItem("mms", null), new ListItem("instant message", null)], ComparisonType.Equals));

      this.submitSearch(searchExpression);
    };

    private readonly command5 = term => {
      const searchExpression = new SearchExpression();

      searchExpression.operator = Operator.And;
      searchExpression.fields.push(new MultiTextField("type", null, [new ListItem("email", null)], ComparisonType.Equals));
      searchExpression.fields.push(new MultiTextField("recipients", null, [new ListItem(term, null)], ComparisonType.Contains));

      this.submitSearch(searchExpression);
    };

    private readonly command6 = term => {
      const searchExpression = new SearchExpression();

      searchExpression.operator = Operator.And;
      searchExpression.fields.push(new MultiTextField("recipients", null, [new ListItem(term, null)], ComparisonType.Contains));
      searchExpression.fields.push(new MultiTextField("type", null, [new ListItem("email", null), new ListItem("sms", null), new ListItem("mms", null), new ListItem("instant message", null)], ComparisonType.Equals));

      this.submitSearch(searchExpression);
    };

    private readonly command7 = term => {
      const searchExpression = new SearchExpression();

      searchExpression.operator = Operator.And;
      searchExpression.fields.push(new MultiTextField("recipients", null, [new ListItem(term, null)], ComparisonType.Contains));
      searchExpression.fields.push(new MultiTextField("type", null, [new ListItem("sms", null), new ListItem("mms", null), new ListItem("instant message", null)], ComparisonType.Equals));

      this.submitSearch(searchExpression);
    };

    private readonly command8 = term => {
      const searchExpression = new SearchExpression();

      searchExpression.operator = Operator.And;
      searchExpression.fields.push(new MultiFullTextField("fullText", null, [new ListItem(term, null)], ComparisonType.Contains));
      searchExpression.fields.push(new MultiTextField("type", null, [new ListItem("email", null)], ComparisonType.Equals));

      this.submitSearch(searchExpression);
    };

    private readonly command9 = term => {
      const searchExpression = new SearchExpression();

      searchExpression.operator = Operator.And;
      searchExpression.fields.push(new MultiFullTextField("fullText", null, [new ListItem(term, null)], ComparisonType.Contains));
      searchExpression.fields.push(new MultiTextField("type", null, [new ListItem("sms", null), new ListItem("mms", null), new ListItem("instant message", null)], ComparisonType.Equals));

      this.submitSearch(searchExpression);
    };

    private readonly commands = {
      "show": this.command1,
      "show me": this.command1,
      "show all": this.command1,
      "show me all": this.command1,
      "show emails from": this.command2,
      "show meemails from": this.command2,
      "show all emails from": this.command2,
      "show me all emails from": this.command2,
      "show communications from": this.command3,
      "show me communications from": this.command3,
      "show all communications from": this.command3,
      "show me all communications from": this.command3,
      "show messages from": this.command4,
      "show me messages from": this.command4,
      "show all messages from": this.command4,
      "show me all messages from": this.command4,
      "show emails to": this.command5,
      "show me emails to": this.command5,
      "show all emails to": this.command5,
      "show me all emails to": this.command5,
      "show communications to": this.command6,
      "show me communications to": this.command6,
      "show all communications to": this.command6,
      "show me all communications to": this.command6,
      "show messages to": this.command7,
      "show me messages to": this.command7,
      "show all messages to": this.command7,
      "show me all messages to": this.command7,
      "show emails containing": this.command8,
      "show me emails containing": this.command8,
      "show all emails containing": this.command8,
      "show me all emails containing": this.command8,
      "show messages containing": this.command9,
      "show me messages containing": this.command9,
      "show all messages containing": this.command9,
      "show me all messages containing": this.command9
    };

    private registerSpeechRecognitionEvents() {
      this.recognition.onresult = (e: SpeechRecognitionEvent) => {
        this.$rootScope.$broadcast("Search.VoiceCommandDetected");

        this.stop();

        if (typeof e.results === "undefined") return;
        const transcript = e.results[e.results.length - 1][0].transcript
          .toLowerCase()
          .trim();

        for (let command in this.commands) {
          if (transcript.indexOf(command) === 0) {
            if (transcript[command.length] === undefined) {
              this.commands[command]();
            } else {
              const param = transcript
                .substring(command.length, transcript.length)
                .trim();
              this.commands[command](param);
            }
          }
        }
      };
    }
  }

  frontendApp.factory("WebSpeechService", ["$rootScope", Factory.Create(WebSpeechService)]);
}
